"use client";

import Link from "next/link";
import { FormEvent, useState } from "react";

import { readErrorMessage } from "../lib/http";

const apiOrigin = process.env.NEXT_PUBLIC_API_ORIGIN ?? "";

type SignupErrors = {
  email?: string;
  name?: string;
  password?: string;
};

export default function SignupPage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("Folio에서 사용할 계정을 만들어 주세요.");
  const [messageType, setMessageType] = useState<"info" | "error" | "success">("info");
  const [errors, setErrors] = useState<SignupErrors>({});
  const [isBusy, setIsBusy] = useState(false);

  async function loginAfterSignup() {
    const response = await fetch(`${apiOrigin}/api/v1/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim(), password }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response));
    }
    const data = (await response.json()) as { access_token: string };
    localStorage.setItem("folio_token", data.access_token);
    window.location.href = "/dashboard";
  }

  function validateSignup() {
    const nextErrors: SignupErrors = {};
    if (!name.trim()) {
      nextErrors.name = "이름을 입력해 주세요.";
    }
    if (!email.trim()) {
      nextErrors.email = "이메일을 입력해 주세요.";
    } else if (!email.includes("@")) {
      nextErrors.email = "올바른 이메일 형식으로 입력해 주세요.";
    }
    if (!password) {
      nextErrors.password = "비밀번호를 입력해 주세요.";
    } else if (password.length < 8) {
      nextErrors.password = "비밀번호는 8자 이상이어야 합니다.";
    }
    setErrors(nextErrors);
    return nextErrors;
  }

  async function submitSignup() {
    const nextErrors = validateSignup();
    if (Object.keys(nextErrors).length) {
      setMessageType("error");
      setMessage("입력 내용을 확인해 주세요.");
      return;
    }
    setIsBusy(true);
    setMessageType("info");
    setMessage("회원가입을 처리하고 있습니다.");
    try {
      const response = await fetch(`${apiOrigin}/api/v1/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), password }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      setMessageType("success");
      setMessage("가입이 완료되었습니다. 대시보드로 이동합니다.");
      await loginAfterSignup();
    } catch (error) {
      setMessageType("error");
      setMessage(error instanceof Error ? error.message : "회원가입에 실패했습니다.");
    } finally {
      setIsBusy(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submitSignup();
  }

  return (
    <main className="grid min-h-screen place-items-center bg-[#f4f7fb] px-4 text-slate-950">
      <form
        action="/api/v1/auth/signup/form"
        method="post"
        noValidate
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6"
      >
        <p className="text-sm font-semibold text-emerald-700">Folio</p>
        <h1 className="mt-2 text-2xl font-semibold">회원가입</h1>
        <p
          className={`mt-4 rounded-md border px-3 py-2 text-sm font-medium ${
            messageType === "error"
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : messageType === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-slate-200 bg-slate-50 text-slate-600"
          }`}
        >
          {message}
        </p>
        <div className="mt-6 grid gap-4">
          <label className="grid gap-2 text-sm font-medium">
            이름
            <input
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setErrors((current) => ({ ...current, name: undefined }));
              }}
              className={`h-11 rounded-md border px-3 font-normal ${
                errors.name ? "border-rose-300 bg-rose-50" : "border-slate-300"
              }`}
              name="name"
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? "signup-name-error" : undefined}
              required
            />
            {errors.name ? (
              <span id="signup-name-error" className="text-xs font-normal text-rose-700">
                {errors.name}
              </span>
            ) : null}
          </label>
          <label className="grid gap-2 text-sm font-medium">
            이메일
            <input
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setErrors((current) => ({ ...current, email: undefined }));
              }}
              className={`h-11 rounded-md border px-3 font-normal ${
                errors.email ? "border-rose-300 bg-rose-50" : "border-slate-300"
              }`}
              type="email"
              name="email"
              autoComplete="email"
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? "signup-email-error" : undefined}
              required
            />
            {errors.email ? (
              <span id="signup-email-error" className="text-xs font-normal text-rose-700">
                {errors.email}
              </span>
            ) : null}
          </label>
          <label className="grid gap-2 text-sm font-medium">
            비밀번호
            <input
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setErrors((current) => ({ ...current, password: undefined }));
              }}
              className={`h-11 rounded-md border px-3 font-normal ${
                errors.password ? "border-rose-300 bg-rose-50" : "border-slate-300"
              }`}
              type="password"
              name="password"
              autoComplete="new-password"
              minLength={8}
              aria-invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? "signup-password-error" : "signup-password-help"}
              required
            />
            {errors.password ? (
              <span id="signup-password-error" className="text-xs font-normal text-rose-700">
                {errors.password}
              </span>
            ) : (
              <span id="signup-password-help" className="text-xs font-normal text-slate-500">
                비밀번호는 8자 이상 입력해 주세요.
              </span>
            )}
          </label>
          <button
            type="submit"
            disabled={isBusy}
            className="h-11 rounded-md bg-slate-950 text-sm font-semibold text-white disabled:bg-slate-400"
          >
            {isBusy ? "가입 처리 중..." : "가입하기"}
          </button>
        </div>
        <p className="mt-5 text-sm text-slate-600">
          이미 계정이 있나요?{" "}
          <Link href="/login" className="font-semibold text-emerald-700">
            로그인
          </Link>
        </p>
      </form>
    </main>
  );
}
